import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";
import queryString from "query-string";
import { OTPInput, REGEXP_ONLY_DIGITS_AND_CHARS } from "input-otp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faLock } from "@fortawesome/free-solid-svg-icons";

import { tvCodeSchema, tvLoginSchema } from "../schema";
import { TV_LOGIN_ERROR, useCompleteTvLogin } from "../api/use-complete-tv-login";
import { ROUTES } from "../../../../constants/route";
import { useMyContext } from "../../../../contexts/StateHolder";
import Button from "../../../../components/Common/Button/Button";
import Loader from "../../../../components/Common/Loader/Loader";
import formClasses from "../../../../components/Form/Form.module.css";
import inputClasses from "../../../../components/Common/Input/Input.module.css";
import classes from "./TvLoginForm.module.css";

const sanitize = (value) => (value ?? "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();

const formatGrouped = (raw) => {
	const clean = sanitize(raw);
	if (clean.length <= 3) return clean;
	return `${clean.slice(0, 3)}-${clean.slice(3, 6)}`;
};

const errorToKey = (code) => {
	switch (code) {
		case TV_LOGIN_ERROR.INVALID_CREDENTIALS:
			return "tvLogin.errors.invalidCredentials";
		case TV_LOGIN_ERROR.EXPIRED:
			return "tvLogin.errors.expired";
		case TV_LOGIN_ERROR.ALREADY_COMPLETED:
			return "tvLogin.errors.alreadyCompleted";
		case TV_LOGIN_ERROR.NOT_FOUND:
			return "tvLogin.errors.notFound";
		case TV_LOGIN_ERROR.UNAUTHORIZED:
			return "tvLogin.errors.unauthorized";
		case TV_LOGIN_ERROR.INVALID_USER:
		case TV_LOGIN_ERROR.INVALID_ORGANIZATION:
		case TV_LOGIN_ERROR.MISSING_PARAMS:
			return "tvLogin.errors.invalid";
		default:
			return "tvLogin.errors.unknown";
	}
};

const FloatingField = ({ id, label, type, icon, error, isTouched, register, value }) => {
	const inputRef = useRef(null);
	const [hasAutofillValue, setHasAutofillValue] = useState(false);
	const { ref, onChange, onBlur, name } = register(id);
	const isFilled = !!value || hasAutofillValue;

	useEffect(() => {
		const input = inputRef.current;
		if (!input) return undefined;

		const syncFilledState = () => {
			setHasAutofillValue(!!input.value);
		};

		const onAnimationStart = (event) => {
			if (event.animationName.includes("onAutoFillStart")) {
				setHasAutofillValue(true);
			}
		};

		syncFilledState();
		const timers = [0, 50, 100, 250, 500, 1000].map((delay) =>
			window.setTimeout(syncFilledState, delay)
		);

		input.addEventListener("input", syncFilledState);
		input.addEventListener("change", syncFilledState);
		input.addEventListener("animationstart", onAnimationStart);

		return () => {
			timers.forEach(clearTimeout);
			input.removeEventListener("input", syncFilledState);
			input.removeEventListener("change", syncFilledState);
			input.removeEventListener("animationstart", onAnimationStart);
		};
	}, []);

	return (
		<div className={classes.floatingField}>
			<div
				className={`${inputClasses.input_container} ${
					error && isTouched ? inputClasses.formInputInvalid : ""
				}`}
			>
				<input
					id={id}
					name={name}
					ref={(element) => {
						ref(element);
						inputRef.current = element;
					}}
					className={inputClasses.input_text}
					type={type}
					placeholder=" "
					autoComplete={id === "email" ? "email" : "current-password"}
					onChange={onChange}
					onBlur={onBlur}
				/>
				<label
					htmlFor={id}
					className={`${inputClasses.input_container_label} ${
						isFilled ? classes.labelFloated : ""
					}`}
				>
					{label}
				</label>
				{icon && <span className={inputClasses.input_icon}>{icon}</span>}
			</div>
			{error && isTouched && (
				<p className={inputClasses.errorText}>{error}</p>
			)}
		</div>
	);
};

const OtpSlot = ({ slot, invalid }) => (
	<div
		className={`${classes.otpSlot} ${slot.isActive ? classes.otpSlotActive : ""} ${
			invalid ? classes.otpSlotInvalid : ""
		}`}
	>
		{slot.char ?? slot.placeholderChar}
		{slot.hasFakeCaret && (
			<div className={classes.otpCaret} aria-hidden="true" />
		)}
	</div>
);

export const TvLoginForm = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const { user, userLoggedIn } = useMyContext();
	const [cookies] = useCookies(["user"]);
	const mutation = useCompleteTvLogin();

	const { code: queryCode } = queryString.parse(location.search);
	const prefilledCode = useMemo(
		() => sanitize(typeof queryCode === "string" ? queryCode : ""),
		[queryCode]
	);

	const sessionUserId = user?.userId ?? cookies?.user?.userId;
	const sessionEmail = user?.eMail ?? cookies?.user?.eMail;
	const sessionName = user?.firstName ?? cookies?.user?.firstName;
	const isLoggedIn = !!sessionUserId;
	const isSessionHydrating = !userLoggedIn && !!cookies?.user?.userId && !user?.userId;

	const formSchema = isLoggedIn ? tvCodeSchema : tvLoginSchema;

	const {
		register,
		control,
		handleSubmit,
		setValue,
		watch,
		formState: { errors, touchedFields },
	} = useForm({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: "",
			password: "",
			code: prefilledCode,
		},
		mode: "onBlur",
		reValidateMode: "onBlur",
	});

	const codeValue = watch("code");
	const emailValue = watch("email");
	const passwordValue = watch("password");

	useEffect(() => {
		if (prefilledCode) {
			setValue("code", prefilledCode, { shouldValidate: false });
		}
	}, [prefilledCode, setValue]);

	const returnPath = `/tv${prefilledCode ? `?code=${formatGrouped(prefilledCode)}` : ""}`;

	const onSubmit = (data) => {
		if (isLoggedIn) {
			mutation.mutate({ userCode: data.code, sessionUserId });
		} else {
			mutation.mutate({
				userCode: data.code,
				email: data.email,
				password: data.password,
			});
		}
	};

	const mutationErrorMessage = mutation.error?.code
		? t(errorToKey(mutation.error.code))
		: null;

	const codeTouched = touchedFields.code;
	const codeError = errors.code?.message ? t(errors.code.message) : null;
	const showCodeHint = !codeError || !codeTouched;

	if (isSessionHydrating) {
		return (
			<div className={classes.loadingSession}>
				<Loader />
				<p>{t("tvLogin.loadingSession")}</p>
			</div>
		);
	}

	if (mutation.isSuccess) {
		return (
			<div className={classes.successState}>
				<div className={classes.successIcon} aria-hidden="true">
					✓
				</div>
				<h2 className={classes.successHeading}>{t("tvLogin.success.heading")}</h2>
				<p className={classes.successBody}>{t("tvLogin.success.body")}</p>
				<Button to={ROUTES.HOME} className={`width-100 ${classes.fullWidthButton}`}>
					{t("tvLogin.success.returnHome")}
				</Button>
			</div>
		);
	}

	return (
		<form className={classes.form} onSubmit={handleSubmit(onSubmit)} noValidate>
			<div className={classes.section}>
				<label className={classes.sectionLabel} htmlFor="tv-pairing-code">
					{t("tvLogin.codeLabel")}
				</label>
				<Controller
					name="code"
					control={control}
					render={({ field }) => (
						<OTPInput
							id="tv-pairing-code"
							maxLength={6}
							value={field.value}
							onChange={(value) => field.onChange(sanitize(value))}
							onBlur={field.onBlur}
							pasteTransformer={sanitize}
							pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
							render={({ slots }) => (
								<div className={classes.otpGroup}>
									<div className={classes.otpSlots} role="presentation">
										{slots.slice(0, 3).map((slot, index) => (
											<OtpSlot
												key={index}
												slot={slot}
												invalid={!!codeError && codeTouched}
											/>
										))}
									</div>
									<span className={classes.otpSeparator} aria-hidden="true">
										-
									</span>
									<div className={classes.otpSlots} role="presentation">
										{slots.slice(3).map((slot, index) => (
											<OtpSlot
												key={index + 3}
												slot={slot}
												invalid={!!codeError && codeTouched}
											/>
										))}
									</div>
								</div>
							)}
						/>
					)}
				/>
				{codeError && codeTouched ? (
					<p className={classes.codeErrorText}>{codeError}</p>
				) : showCodeHint ? (
					<p className={classes.sectionHint}>{t("tvLogin.codeHint")}</p>
				) : null}
			</div>

			{isLoggedIn ? (
				<>
					<div className={classes.signedInBanner}>
						<p className={classes.signedInLabel}>{t("tvLogin.signedInAs")}</p>
						<p className={classes.signedInName}>
							{sessionName || sessionEmail}
						</p>
					</div>
					<p className={classes.confirmHint}>
						{t("tvLogin.confirmHint", {
							code: formatGrouped(codeValue) || "———",
						})}
					</p>
					{mutationErrorMessage && (
						<p className={classes.mutationError}>{mutationErrorMessage}</p>
					)}
					<div className={classes.actions}>
						<Button
							type="submit"
							className={`width-100 ${classes.fullWidthButton}`}
							disabled={mutation.isLoading}
							formButton
						>
							{mutation.isLoading
								? t("tvLogin.confirming")
								: t("tvLogin.confirmButton")}
						</Button>
						<Button
							to={ROUTES.HOME}
							inverse
							className={`width-100 ${classes.fullWidthButton}`}
						>
							{t("tvLogin.cancel")}
						</Button>
					</div>
				</>
			) : (
				<>
					<p className={classes.sectionLabel}>{t("tvLogin.credentialsLabel")}</p>

					<FloatingField
						id="email"
						label={t("form.password.email")}
						type="email"
						icon={<FontAwesomeIcon icon={faEnvelope} />}
						error={errors.email?.message ? t(errors.email.message) : null}
						isTouched={touchedFields.email}
						register={register}
						value={emailValue}
					/>

					<FloatingField
						id="password"
						label={t("form.password.password")}
						type="password"
						icon={<FontAwesomeIcon icon={faLock} />}
						error={errors.password?.message ? t(errors.password.message) : null}
						isTouched={touchedFields.password}
						register={register}
						value={passwordValue}
					/>

					{mutationErrorMessage && (
						<p className={classes.mutationError}>{mutationErrorMessage}</p>
					)}

					<Button
						type="submit"
						className={`width-100 ${classes.fullWidthButton}`}
						disabled={mutation.isLoading}
						formButton
					>
						{mutation.isLoading ? t("shared.loading") : t("tvLogin.confirmButton")}
					</Button>

					<div className={`${formClasses.linkContainer} font-200`}>
						<div>
							{t("tvLogin.useLoginPage")}{" "}
							<Link
								to={{
									pathname: ROUTES.LOGIN,
									state: { from: { pathname: returnPath } },
								}}
								className={formClasses.linkText}
							>
								{t("shared.login")}
							</Link>
						</div>
					</div>
				</>
			)}
		</form>
	);
};

export default TvLoginForm;
